import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of, shareReplay, catchError, map, tap } from 'rxjs';
import { API_BASE_URL } from '@core/config/api.config';
import { ApiEndpoints } from '@core/config/api-endpoints';
import { AuthSessionService } from './auth-session.service';
import { mapAuthUserDtoToDomain, type AuthUserDto } from '@features/auth/public-api';

interface RefreshResponseDto {
  token?: string;
  access_token?: string;
  refresh_token?: string;
  session_id?: number;
  user?: AuthUserDto;
}

/**
 * Un solo refresh en vuelo ante 401; best-effort sin UI.
 */
@Injectable({ providedIn: 'root' })
export class AuthTokenRefreshService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);
  private readonly authSession = inject(AuthSessionService);
  private inFlight: Observable<boolean> | null = null;

  tryRefresh(): Observable<boolean> {
    const refresh = this.authSession.refreshToken();
    if (!refresh) {
      return of(false);
    }
    if (this.inFlight) {
      return this.inFlight;
    }

    const epoch = this.authSession.epoch();

    this.inFlight = this.http
      .post<RefreshResponseDto>(`${this.baseUrl}${ApiEndpoints.auth.refresh}`, {
        refresh_token: refresh,
      })
      .pipe(
        map((dto) => {
          if (this.authSession.epoch() !== epoch) {
            // La sesión se cerró mientras esta petición estaba en vuelo: descartar
            // el resultado para no "revivir" tokens tras un logout.
            return false;
          }
          const token = (dto.token ?? dto.access_token ?? '').trim();
          if (!token) {
            return false;
          }
          if (dto.user) {
            this.authSession.setSession(token, mapAuthUserDtoToDomain(dto.user), dto.refresh_token, dto.session_id);
          } else {
            this.authSession.setTokens(token, dto.refresh_token, dto.session_id);
          }
          return true;
        }),
        catchError(() => of(false)),
        tap(() => {
          this.inFlight = null;
        }),
        shareReplay(1)
      );

    return this.inFlight;
  }
}
